import * as THREE from 'three'

import Experience from './Experience.js'
import { works, mediastorm, profile } from './zhuyuData.js'

const SLIDE_MS = 4200

/* 用 canvas 贴图替换原视频屏幕：mode = 'mediastorm'（PC 大屏）| 'works'（Mac 屏） */
export default class ZhuyuScreen
{
    constructor(_mesh, _mode)
    {
        this.experience = new Experience()
        this.time = this.experience.time
        this.world = this.experience.world

        this.mesh = _mesh
        this.mode = _mode
        this.index = 0
        this.lastSwitch = 0

        this.covers = []
        if(this.mode === 'works')
        {
            for(const w of works)
            {
                const img = new Image()
                img.src = `/assets/zhuyu/covers/${w.id}.jpeg`
                img.onload = () => { if(this.index === works.indexOf(w)) this.draw() }
                this.covers.push(img)
            }
        }
        else
        {
            for(const v of mediastorm)
            {
                const img = new Image()
                img.src = v.img
                img.onload = () => { if(this.index % mediastorm.length === mediastorm.indexOf(v)) this.draw() }
                this.covers.push(img)
            }
        }

        this.setModel()
        this.draw()
    }

    setModel()
    {
        this.canvas = document.createElement('canvas')
        this.canvas.width = 1280
        this.canvas.height = 720
        this.ctx = this.canvas.getContext('2d')

        this.texture = new THREE.CanvasTexture(this.canvas)
        this.texture.encoding = THREE.sRGBEncoding
        /* 房间整体镜像，贴图水平翻转纠正文字方向 */
        this.texture.wrapS = THREE.RepeatWrapping
        this.texture.center.set(0.5, 0.5)
        this.texture.repeat.x = -1

        this.material = new THREE.MeshBasicMaterial({ map: this.texture, side: THREE.DoubleSide })
        this.mesh.material = this.material
        this.experience.world.group.add(this.mesh)
    }

    draw()
    {
        if(this.mode === 'mediastorm')
            this.drawMediastorm()
        else
            this.drawWorks()

        this.texture.needsUpdate = true
    }

    drawMediastorm()
    {
        const c = this.ctx
        const W = 1280, H = 720
        const v = mediastorm[this.index % mediastorm.length]

        const ORANGE = '#ff5000'
        const i = this.index % mediastorm.length
        const img = this.covers[i]

        /* 真实封面铺满（已离线像素粗糙化） */
        c.fillStyle = '#000000'
        c.fillRect(0, 0, W, H)
        if(img && img.complete && img.naturalWidth > 0)
            c.drawImage(img, 0, 0, W, H)

        /* 顶部：追更标签 */
        const top = c.createLinearGradient(0, 0, 0, 120)
        top.addColorStop(0, 'rgba(0,0,0,.72)')
        top.addColorStop(1, 'rgba(0,0,0,0)')
        c.fillStyle = top
        c.fillRect(0, 0, W, 120)
        c.fillStyle = '#ff4d45'
        c.font = 'bold 30px "PingFang SC", "Microsoft YaHei", sans-serif'
        c.fillText('● 追更中', 44, 62)
        c.fillStyle = '#c9c9d2'
        c.font = '28px monospace'
        c.textAlign = 'right'
        c.fillText('bilibili · ' + v.play + '播放', W - 44, 62)
        c.textAlign = 'left'

        /* 底部：影视飓风品牌条（黑场 + 双弧 logo + 片名 + 进度） */
        const bar = c.createLinearGradient(0, H - 210, 0, H)
        bar.addColorStop(0, 'rgba(0,0,0,0)')
        bar.addColorStop(0.45, 'rgba(0,0,0,.82)')
        bar.addColorStop(1, 'rgba(0,0,0,.95)')
        c.fillStyle = bar
        c.fillRect(0, H - 210, W, 210)

        const lx = 92, ly = H - 108, R = 34
        c.strokeStyle = ORANGE
        c.lineWidth = 16
        c.lineCap = 'round'
        c.beginPath(); c.arc(lx, ly, R, -Math.PI * 0.42, Math.PI * 0.42); c.stroke()
        c.beginPath(); c.arc(lx, ly, R, Math.PI * 0.58, Math.PI * 1.42); c.stroke()
        c.fillStyle = ORANGE
        c.beginPath(); c.arc(lx, ly, 13, 0, 7); c.fill()

        c.fillStyle = '#ffffff'
        c.font = 'bold 40px "PingFang SC", "Microsoft YaHei", sans-serif'
        c.fillText('影视飓风', 152, ly - 8)
        c.fillStyle = ORANGE
        c.font = 'bold 22px Arial, sans-serif'
        c.fillText('M E D I A S T O R M', 152, ly + 26)

        c.fillStyle = '#e8e8ee'
        c.font = '30px "PingFang SC", "Microsoft YaHei", sans-serif'
        this.wrapText(c, v.title, 460, ly - 2, W - 520, 40)

        const pct = (this.time.elapsed % 18000) / 18000
        c.fillStyle = 'rgba(255,255,255,.16)'
        c.fillRect(0, H - 10, W, 10)
        c.fillStyle = ORANGE
        c.fillRect(0, H - 10, W * pct, 10)
    }

    drawWorks()
    {
        const c = this.ctx
        const W = 1280, H = 720
        const i = this.index % works.length
        const w = works[i]
        const img = this.covers[i]

        c.fillStyle = '#101018'
        c.fillRect(0, 0, W, H)

        /* 左：封面 */
        if(img && img.complete && img.naturalWidth > 0)
        {
            const targetH = H - 120
            const targetW = targetH * (img.naturalWidth / img.naturalHeight)
            c.save()
            c.shadowColor = 'rgba(0,0,0,.6)'
            c.shadowBlur = 40
            c.drawImage(img, 70, 60, targetW, targetH)
            c.restore()
        }

        /* 右：信息 */
        const x = 620
        c.fillStyle = '#8a8a96'
        c.font = '30px monospace'
        c.fillText('剪辑时间线 · ' + (i + 1) + ' / ' + works.length, x, 130)

        c.fillStyle = '#ededf2'
        c.font = 'bold 60px "PingFang SC", "Microsoft YaHei", sans-serif'
        this.wrapText(c, w.title, x, 240, 570, 76)

        c.fillStyle = '#ff6a00'
        c.font = 'bold 44px monospace'
        c.fillText('♥ ' + w.likes.toLocaleString(), x, 440)

        c.fillStyle = '#8a8a96'
        c.font = '32px "PingFang SC", "Microsoft YaHei", sans-serif'
        c.fillText(w.tag, x, 510)

        /* 进度条 */
        c.fillStyle = '#23232f'
        c.fillRect(x, 580, 570, 10)
        c.fillStyle = '#ff6a00'
        c.fillRect(x, 580, 570 * ((i + 1) / works.length), 10)

        c.fillStyle = '#55555f'
        c.font = '26px monospace'
        c.fillText('抖音 @' + profile.douyinId, x, 660)
    }

    wrapText(c, text, x, y, maxWidth, lineHeight)
    {
        let line = ''
        let yy = y
        for(const ch of text)
        {
            if(c.measureText(line + ch).width > maxWidth)
            {
                c.fillText(line, x, yy)
                line = ch
                yy += lineHeight
            }
            else
            {
                line += ch
            }
        }
        c.fillText(line, x, yy)
    }

    update()
    {
        if(this.time.elapsed - this.lastSwitch > SLIDE_MS)
        {
            this.lastSwitch = this.time.elapsed
            this.index++
            this.draw()
        }
    }
}
